"use strict";
//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmptyMarkup = void 0;
exports.isEmptyMarkup = isEmptyMarkup;
exports.areEqualMarkups = areEqualMarkups;
exports.areEqualJson = areEqualJson;
exports.equalMarks = equalMarks;
exports.isEmptyNode = isEmptyNode;
exports.jsonToMarkup = jsonToMarkup;
exports.markupToJSON = markupToJSON;
exports.stripTags = stripTags;
exports.markupToText = markupToText;
exports.stripHash = stripHash;
exports.hashAttrs = hashAttrs;
const fast_equals_1 = require("fast-equals");
const hash_it_1 = require("hash-it");
const dsl_1 = require("./dsl");
const model_1 = require("./model");
const traverse_1 = require("./traverse");
/** @public */
exports.EmptyMarkup = jsonToMarkup((0, model_1.emptyMarkupNode)());
/** @public */
function isEmptyMarkup(markup) {
    if (markup === undefined || markup === null || markup === '') {
        return true;
    }
    return isEmptyNode(markupToJSON(markup));
}
/** @public */
function areEqualMarkups(markup1, markup2) {
    if (markup1 === markup2) {
        return true;
    }
    const node1 = markupToJSON(markup1);
    const node2 = markupToJSON(markup2);
    if (isEmptyNode(node1) && isEmptyNode(node2)) {
        return true;
    }
    return equalNodes(node1, node2);
}
/** @public */
function areEqualJson(json1, json2) {
    return equalNodes(json1, json2);
}
function equalNodes(node1, node2) {
    if (node1.type !== node2.type)
        return false;
    const text1 = node1.text ?? '';
    const text2 = node2.text ?? '';
    if (text1 !== text2)
        return false;
    if (!equalArrays(node1.content, node2.content, equalNodes))
        return false;
    if (!equalArrays(node1.marks, node2.marks, equalMarks))
        return false;
    if (!equalRecords(node1.attrs, node2.attrs))
        return false;
    return true;
}
function equalArrays(a, b, equal) {
    if (a === b)
        return true;
    const arr1 = a ?? [];
    const arr2 = b ?? [];
    if (arr1.length !== arr2.length)
        return false;
    return arr1.every((item1, i) => equal(item1, arr2[i]));
}
function equalRecords(a, b) {
    if (a === b)
        return true;
    a = Object.fromEntries(Object.entries(a ?? {}).filter(([_, v]) => v != null));
    b = Object.fromEntries(Object.entries(b ?? {}).filter(([_, v]) => v != null));
    return (0, fast_equals_1.deepEqual)(a, b);
}
function equalMarks(a, b) {
    return a.type === b.type && equalRecords(a.attrs, b.attrs);
}
const emptyNodes = [model_1.MarkupNodeType.hard_break];
const nonEmptyNodes = [
    model_1.MarkupNodeType.horizontal_rule,
    model_1.MarkupNodeType.image,
    model_1.MarkupNodeType.reference,
    model_1.MarkupNodeType.emoji,
    model_1.MarkupNodeType.subLink,
    model_1.MarkupNodeType.table
];
/** @public */
function isEmptyNode(node) {
    if (emptyNodes.includes(node.type))
        return true;
    if (nonEmptyNodes.includes(node.type))
        return false;
    if (node.text !== undefined && node.text?.trim().length > 0)
        return false;
    const content = node.content ?? [];
    return content.every(isEmptyNode);
}
// Markup
/** @public */
function jsonToMarkup(json) {
    return JSON.stringify(json);
}
/** @public */
function markupToJSON(markup) {
    if (markup == null || markup === '') {
        return (0, model_1.emptyMarkupNode)();
    }
    try {
        // Ideally Markup should contain only serialized JSON
        // But there seem to be some cases when it contains HTML or plain text
        // So we need to handle those cases and produce valid MarkupNode
        if (markup.startsWith('{')) {
            const json = JSON.parse(markup);
            (0, traverse_1.traverseAllMarks)(json, (node, mark) => {
                mark.type = stripHash(mark.type);
            });
            return json;
        }
        else {
            return (0, dsl_1.nodeDoc)((0, dsl_1.nodeParagraph)((0, dsl_1.nodeText)(markup)));
        }
    }
    catch (error) {
        return (0, model_1.emptyMarkupNode)();
    }
}
// UTILS
const ELLIPSIS_CHAR = '…';
const WHITESPACE = ' ';
/** @public */
function stripTags(markup, textLimit = 0) {
    const parsed = markupToJSON(markup);
    const textParts = [];
    let charCount = 0;
    let isHardStop = false;
    const pushText = (text) => {
        if (textLimit > 0 && charCount + text.length > textLimit) {
            const toAddCount = textLimit - charCount;
            const textPart = text.substring(0, toAddCount);
            textParts.push(textPart);
            textParts.push(ELLIPSIS_CHAR);
            isHardStop = true;
        }
        else {
            textParts.push(text);
            charCount += text.length;
        }
    };
    (0, traverse_1.traverseNode)(parsed, (node, parent) => {
        if (isHardStop) {
            return false;
        }
        if (node.type === model_1.MarkupNodeType.text) {
            const text = node.text ?? '';
            pushText(text);
            return false;
        }
        else if (node.type === model_1.MarkupNodeType.paragraph ||
            node.type === model_1.MarkupNodeType.table ||
            node.type === model_1.MarkupNodeType.doc ||
            node.type === model_1.MarkupNodeType.blockquote) {
            if (textParts.length > 0 && textParts[textParts.length - 1] !== WHITESPACE) {
                textParts.push(WHITESPACE);
                charCount++;
            }
        }
        else if (node.type === model_1.MarkupNodeType.reference) {
            const label = `${node.attrs?.label ?? ''}`;
            pushText(label.length > 0 ? `@${label}` : '');
        }
        return true;
    });
    const result = textParts.join('');
    return result;
}
function markupToText(markup) {
    const jsonModel = markupToJSON(markup);
    const fragments = [];
    (0, traverse_1.traverseNode)(jsonModel, (node) => {
        if (node.type === model_1.MarkupNodeType.text) {
            const text = node.text ?? '';
            if (node.text !== undefined && node.text.length > 0) {
                fragments.push(text);
            }
        }
        else if (node.type === model_1.MarkupNodeType.paragraph) {
            fragments.push('\n\n');
        }
        else if (node.type === model_1.MarkupNodeType.reference) {
            const label = `${node.attrs?.label ?? ''}`;
            fragments.push(label.length > 0 ? `@${label}` : '');
        }
        return true;
    });
    return fragments.join('').trim();
}
// see https://github.com/yjs/y-prosemirror/blob/master/src/lib.js#L402
const hashedMarkNameRegex = /(.*)(--[a-zA-Z0-9+/=]{8})$/;
/** @public */
function stripHash(attrName) {
    return hashedMarkNameRegex.exec(attrName)?.[1] ?? attrName;
}
/** @public */
function hashAttrs(attrs) {
    const hash = (0, hash_it_1.hash)(attrs);
    return (hash >>> 0).toString(16).padStart(8, '0');
}
//# sourceMappingURL=utils.js.map